/**
 * 悬浮窗位置接口
 */
export interface FloatingMenuPosition {
  left: number
  top: number
  timestamp?: number // 可选：记录保存时间
}

/**
 * 悬浮窗存储配置
 */
export interface StorageConfig {
  key?: string // 存储键名
  expireTime?: number
}

/**
 * 悬浮窗位置存储工具类
 */
export class FloatingMenuStorage {
  private readonly storageKey: string
  private readonly expireTime: number

  constructor(config: StorageConfig = {}) {
    this.storageKey = config.key || 'floating_menu_position'
    this.expireTime = config.expireTime || 0 // 默认永不过期
  }

  /**
   * 保存悬浮窗位置
   * @param position 位置信息
   * @returns 是否保存成功
   */
  savePosition(position: FloatingMenuPosition): boolean {
    try {
      const data: FloatingMenuPosition = {
        ...position,
        timestamp: Date.now()
      }
      localStorage.setItem(this.storageKey, JSON.stringify(data))
      return true
    } catch (error) {
      console.error('保存悬浮窗位置失败:', error)
      return false
    }
  }

  /**
   * 获取悬浮窗位置
   * @returns 位置信息，如果不存在或已过期则返回 null
   */
  getPosition(): FloatingMenuPosition | null {
    try {
      const data = localStorage.getItem(this.storageKey)
      if (!data) return null

      const position: FloatingMenuPosition = JSON.parse(data)

      // 检查是否过期
      if (this.expireTime > 0 && position.timestamp) {
        const now = Date.now()
        if (now - position.timestamp > this.expireTime) {
          this.clearPosition()
          return null
        }
      }

      return position
    } catch (error) {
      console.error('获取悬浮窗位置失败:', error)
      return null
    }
  }

  /**
   * 清除保存的位置
   * @returns 是否清除成功
   */
  clearPosition(): boolean {
    try {
      localStorage.removeItem(this.storageKey)
      return true
    } catch (error) {
      console.error('清除悬浮窗位置失败:', error)
      return false
    }
  }

  /**
   * 检查是否有保存的位置
   * @returns 是否存在有效的位置数据
   */
  hasPosition(): boolean {
    return this.getPosition() !== null
  }

  /**
   * 更新位置（仅更新指定字段）
   * @param partial 部分位置信息
   * @returns 是否更新成功
   */
  updatePosition(partial: Partial<FloatingMenuPosition>): boolean {
    const current = this.getPosition()
    if (!current) return false

    return this.savePosition({
      ...current,
      ...partial
    })
  }
}

/**
 * 默认实例（单例模式）
 */
export const floatingMenuStorage = new FloatingMenuStorage()

/**
 * 快捷函数：保存位置
 */
export const saveFloatingPosition = (left: number, top: number): boolean => {
  return floatingMenuStorage.savePosition({ left, top })
}

/**
 * 快捷函数：获取位置
 */
export const getFloatingPosition = (): FloatingMenuPosition | null => {
  return floatingMenuStorage.getPosition()
}

/**
 * 快捷函数：清除位置
 */
export const clearFloatingPosition = (): boolean => {
  return floatingMenuStorage.clearPosition()
}
